import axios from "axios";
import { Platform } from "react-native";
import { getToken } from "./auth.api";
import {
  isAiTokenExpired,
  isRefreshTokenExpired,
  isRestTokenExpired,
} from "./auth-error";
import {
  coordinatedRefresh,
  handleAuthFailure,
  MAX_REFRESH_ATTEMPTS,
} from "./token-refresh";

const isWeb = Platform.OS === "web";

const apiClient = axios.create({
  baseURL: process.env.EXPO_PUBLIC_BLOCKS_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "x-blocks-key": process.env.EXPO_PUBLIC_X_BLOCKS_KEY,
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    config.headers = config.headers ?? {};
    config.headers["x-blocks-key"] = `${process.env.EXPO_PUBLIC_X_BLOCKS_KEY}`;
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (!isWeb) {
      console.warn("No auth token found");
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      !originalRequest ||
      originalRequest.url?.includes("/Token") ||
      originalRequest._skipAuthRefresh
    ) {
      return Promise.reject(error);
    }

    const isAiEndpoint = originalRequest.url?.includes("/blocksai-api");
    const shouldRefresh = isAiEndpoint
      ? isAiTokenExpired(error.response?.data)
      : isRestTokenExpired(error.response);

    if (!shouldRefresh) {
      return Promise.reject(error);
    }

    originalRequest._refreshAttempts = originalRequest._refreshAttempts ?? 0;
    if (originalRequest._refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      return Promise.reject(error);
    }
    originalRequest._refreshAttempts += 1;

    try {
      await coordinatedRefresh();
    } catch (refreshError) {
      if (isRefreshTokenExpired(refreshError)) {
        await handleAuthFailure();
      }
      return Promise.reject(refreshError);
    }

    return apiClient(originalRequest);
  },
);

export default apiClient;
